import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaArrowLeft,
  FaNetworkWired,
  FaCube,
  FaFileInvoice,
  FaCalendarAlt,
  FaPlug,
  FaChartBar,
  FaMapMarkerAlt,
  FaHardHat,
  FaMap,
  FaPrint,
  FaDownload,
  FaCircle,
  FaHistory,
  FaExclamationTriangle,
  FaClock,
  FaSpinner,
} from 'react-icons/fa';
import { Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { fetchKavachDeviceById } from '../utils/kavachApiService';
import { KavachSwitch } from '../utils/kavachTypes';
import { KAVACH_COLORS } from '../utils/kavachConstants';
import '../kavach-dashboard.scss';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend);

type DetailTab = 'overview' | 'network' | 'location' | 'performance';

const KavachSwitchDetail: React.FC = () => {
  // const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('deviceId');
  const name = searchParams.get('deviceName');
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [switchData, setSwitchData] = useState<KavachSwitch | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch device data on mount
  useEffect(() => {
    const loadDeviceData = async () => {
      if (!id || !name) {
        setError('No device ID provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const result = await fetchKavachDeviceById({ deviceId: id, deviceName: name });
        if (result?.switch) {
          setSwitchData(result.switch);
        } else {
          setError('Device not found');
        }
      } catch (err) {
        console.error('Error loading device:', err);
        setError('Failed to load device data');
      } finally {
        setLoading(false);
      }
    };

    loadDeviceData();
  }, [id]);

  console.log(switchData, "switchData");

  // Calculate health based on port status
  const getHealth = (data: KavachSwitch | null): string => {
    if (!data) return 'partial';
    const ports = Object.values(data.ports);
    const downCount = ports.filter(p => p.status === 'DOWN').length;
    if (downCount === 0) return 'healthy';
    if (downCount >= 3) return 'critical';
    return 'partial';
  };

  const health = getHealth(switchData);

  const getHealthBadgeClass = (h: string) => {
    switch (h) {
      case 'healthy': return 'health-badge healthy';
      case 'partial': return 'health-badge partial';
      case 'critical': return 'health-badge critical';
      default: return 'health-badge';
    }
  };

  // Performance chart data
  const performanceChartData = {
    labels: ['6h ago', '5h ago', '4h ago', '3h ago', '2h ago', '1h ago', 'Now'],
    datasets: [
      {
        label: 'Latency (ms)',
        data: [12, 14, 11, 15, 13, 12, switchData?.assetDetails.avgLatency ? parseInt(switchData.assetDetails.avgLatency) : 12],
        borderColor: KAVACH_COLORS.primary,
        backgroundColor: 'rgba(0, 86, 179, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
    },
    scales: {
      y: { beginAtZero: true, max: 50 },
    },
  };

  const handlePrint = () => window.print();

  const handleExport = () => {
    alert('PDF export functionality would be implemented here');
  };

  const tabs: { id: DetailTab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <FaCube /> },
    { id: 'network', label: 'Network', icon: <FaNetworkWired /> },
    { id: 'location', label: 'Location', icon: <FaMapMarkerAlt /> },
    { id: 'performance', label: 'Performance', icon: <FaChartBar /> },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="kavach-dashboard">
        <div className="detail-page">
          <div className="loading-container">
            <FaSpinner className="spinner" />
            <p>Loading device data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error or not found state
  if (error || !switchData) {
    return (
      <div className="kavach-dashboard">
        <div className="detail-page">
          <div className="no-data">
            <FaExclamationTriangle className="no-data-icon" />
            <h3>{error || 'Switch not found'}</h3>
            <button className="back-btn" onClick={() => navigate('/kavach-dashboard')}>
              <FaArrowLeft /> Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { assetDetails, ports } = switchData;

  return (
    <div className="kavach-dashboard">
      <div className="detail-page">
        {/* Header */}
        <div className="detail-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => navigate('/kavach-dashboard')}>
              <FaArrowLeft />
            </button>
            <div className="header-info">
              <h1>
                <FaNetworkWired />
                {switchData.name}
              </h1>
              <div className="header-meta">
                <span className="station-badge">{switchData.station}</span>
                <span className="device-type-badge">{switchData.deviceType}</span>
                <span className={getHealthBadgeClass(health)}>
                  {health === 'healthy' ? 'All Ports Up' : health === 'partial' ? 'Partial Issues' : 'Critical'}
                </span>
                <span className="ip-badge">{assetDetails.ipAddress}</span>
              </div>
            </div>
          </div>
          <div className="header-actions">
            <button className="view-btn" onClick={() => navigate(`/kavach-switch-history/${id}`)}>
              <FaHistory /> View History
            </button>
            <button className="view-btn" onClick={handlePrint}>
              <FaPrint /> Print
            </button>
            <button className="view-btn primary" onClick={handleExport}>
              <FaDownload /> Export PDF
            </button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="detail-stats">
          <div className="detail-stat-card">
            <div className="stat-icon up"><FaPlug /></div>
            <div className="stat-info">
              <span className="stat-value">{Object.values(ports).filter(p => p.status === 'UP').length}</span>
              <span className="stat-label">Ports Up</span>
            </div>
          </div>
          <div className="detail-stat-card">
            <div className="stat-icon down"><FaPlug /></div>
            <div className="stat-info">
              <span className="stat-value">{Object.values(ports).filter(p => p.status === 'DOWN').length}</span>
              <span className="stat-label">Ports Down</span>
            </div>
          </div>
          <div className="detail-stat-card">
            <div className="stat-icon warning"><FaClock /></div>
            <div className="stat-info">
              <span className="stat-value">{assetDetails.avgLatency || 'N/A'}</span>
              <span className="stat-label">Avg Latency</span>
            </div>
          </div>
          <div className="detail-stat-card">
            <div className="stat-icon info"><FaClock /></div>
            <div className="stat-info">
              <span className="stat-value">{switchData.lastActivity}</span>
              <span className="stat-label">Last Activity</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="detail-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`detail-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="detail-content">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="detail-grid">
              <div className="detail-section">
                <h3><FaCube /> Basic Information</h3>
                <div className="info-list">
                  <div className="info-row">
                    <span className="info-label">Device Type</span>
                    <span className="info-value highlight">{switchData.deviceType}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Make</span>
                    <span className="info-value">{assetDetails.make}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Model</span>
                    <span className="info-value">{assetDetails.model}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Serial Number</span>
                    <span className="info-value highlight">{assetDetails.serialNumber}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Manufacture Number</span>
                    <span className="info-value">{assetDetails.manufactureNumber}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Firmware Version</span>
                    <span className="info-value">{assetDetails.firmwareVersion}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3><FaFileInvoice /> Purchase & Warranty</h3>
                <div className="info-list">
                  <div className="info-row">
                    <span className="info-label">Purchase Order No</span>
                    <span className="info-value">{assetDetails.purchaseOrderNo}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Purchase Date</span>
                    <span className="info-value">{assetDetails.purchaseDate}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Installation Date</span>
                    <span className="info-value">{assetDetails.installationDate}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Warranty Expiry</span>
                    <span className="info-value">{assetDetails.warrantyExpiry}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Warranty Status</span>
                    <span className={`info-value ${assetDetails.warrantyStatus === 'Active' ? 'success' : 'danger'}`}>
                      {assetDetails.warrantyStatus}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3><FaCalendarAlt /> Lifecycle Information</h3>
                <div className="info-list">
                  <div className="info-row">
                    <span className="info-label">Codal Life</span>
                    <span className="info-value">{assetDetails.codalLife}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Remaining Life</span>
                    <span className="info-value success">{assetDetails.remainingLife}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Installation Age</span>
                    <span className="info-value">{assetDetails.installationAge}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Last Activity</span>
                    <span className="info-value">{switchData.lastActivity}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Network Tab */}
          {activeTab === 'network' && (
            <div className="detail-grid">
              <div className="detail-section">
                <h3><FaNetworkWired /> Network Configuration</h3>
                <div className="info-list">
                  <div className="info-row">
                    <span className="info-label">IP Address</span>
                    <span className="info-value highlight">{assetDetails.ipAddress}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">MAC Address</span>
                    <span className="info-value">{assetDetails.macAddress}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Firmware Version</span>
                    <span className="info-value">{assetDetails.firmwareVersion}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Average Latency</span>
                    <span className="info-value">{assetDetails.avgLatency}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section full-width">
                <h3><FaPlug /> Port Status</h3>
                <div className="port-grid">
                  {Object.entries(ports).map(([portKey, port]) => (
                    <div key={portKey} className={`port-card ${port.status.toLowerCase()}`}>
                      <div className="port-header">
                        <FaCircle className={`port-indicator ${port.status.toLowerCase()}`} />
                        <span className="port-name">{portKey.replace('port', 'Port ')}</span>
                      </div>
                      <div className="port-details">
                        <span className="port-status">{port.status}</span>
                        <span className="port-speed">{port.speed}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Location Tab */}
          {activeTab === 'location' && (
            <div className="detail-grid">
              <div className="detail-section">
                <h3><FaMapMarkerAlt /> Location Details</h3>
                <div className="info-list">
                  <div className="info-row">
                    <span className="info-label">Station</span>
                    <span className="info-value highlight">{switchData.station}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Latitude</span>
                    <span className="info-value">{assetDetails.latitude}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Longitude</span>
                    <span className="info-value">{assetDetails.longitude}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Asset Position</span>
                    <span className="info-value">{assetDetails.assetPosition}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Platform No</span>
                    <span className="info-value">{assetDetails.platformNo}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3><FaHardHat /> Installation Details</h3>
                <div className="info-list">
                  <div className="info-row">
                    <span className="info-label">Installation Team</span>
                    <span className="info-value">{assetDetails.installationTeam}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Supervisor</span>
                    <span className="info-value">{assetDetails.supervisor}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Installation Date</span>
                    <span className="info-value">{assetDetails.installationDate}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section full-width">
                <h3><FaMap /> Location Map</h3>
                <div className="map-placeholder">
                  <FaMapMarkerAlt className="map-icon" />
                  <p>Location: {switchData.station} Station</p>
                  <p>Coordinates: {assetDetails.latitude}, {assetDetails.longitude}</p>
                  <p className="small">Map integration available with GPS coordinates</p>
                </div>
              </div>
            </div>
          )}

          {/* Performance Tab */}
          {activeTab === 'performance' && (
            <div className="detail-grid">
              <div className="detail-section full-width">
                <h3><FaChartBar /> Latency Trend (Last 6 Hours)</h3>
                <div className="chart-wrapper">
                  <Line data={performanceChartData} options={chartOptions} />
                </div>
              </div>

              <div className="detail-section">
                <h3>Current Metrics</h3>
                <div className="metrics-grid">
                  <div className="metric-card">
                    <span className="metric-value">{assetDetails.avgLatency}</span>
                    <span className="metric-label">Avg Latency</span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-value">{Object.values(ports).filter(p => p.status === 'UP').length}/4</span>
                    <span className="metric-label">Ports Up</span>
                  </div>
                  <div className="metric-card">
                    <span className="metric-value">{switchData.lastActivity}</span>
                    <span className="metric-label">Last Activity</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Port Performance</h3>
                <div className="port-performance">
                  {Object.entries(ports).map(([portKey, port]) => (
                    <div key={portKey} className="port-perf-row">
                      <span className="port-name">{portKey.replace('port', 'Port ')}</span>
                      <div className="port-bar">
                        <div
                          className={`port-bar-fill ${port.status.toLowerCase()}`}
                          style={{ width: port.status === 'UP' ? '100%' : '0%' }}
                        />
                      </div>
                      <span className={`port-percent ${port.status.toLowerCase()}`}>
                        {port.status === 'UP' ? '100%' : '0%'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KavachSwitchDetail;
